// src/pages/task/TaskEditPage.tsx
// Changing a task that already exists.
//
// The form itself is `TaskFormComposer` — the same component the create page
// renders, filled from the loaded task by the same hook. Editing used to be a
// second implementation of the same screen, which is how it ended up with
// different controls, a different layout, and no idea what the task type asks
// for. There is one form now; this page owns only what is true about
// *editing*: which task, a Save button, a way to delete it, and a question
// before unsaved changes are walked away from.
//
// There is deliberately no draft here. A draft exists to rescue what was never
// written down; these values are already saved, and restoring a stale copy of
// them over newer ones would be a loss dressed up as a rescue. The guard is a
// question on the way out instead.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useTask } from '@react/hooks';

import { Alert, Button, ErrorAlert } from '@react/components';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FORM_ICONS, Icon, TaskFormComposer, useTaskForm } from '@/features/task/form';

/** DOM id of the title field — also the focus target when the title is invalid. */
const TITLE_FIELD_ID = 'task-edit-title';

export const TaskEditPage = observer(() => {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTask();
  const { t } = useI18n();

  const f = useTaskForm({
    mode: 'edit',
    taskId: id,
    titleFieldId: TITLE_FIELD_ID,
    // Saved changes belong to the task, so that is where this goes back to.
    onSaved: (taskId) => navigate(`/tasks/${taskId}`),
  });

  const [confirmLeave, setConfirmLeave] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const leave = () => navigate(id ? `/tasks/${id}` : '/tasks');

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await store.DeleteTask(id);
      navigate('/tasks');
    } catch {
      // The store surfaces the failure; the form stays as it was.
    } finally {
      setIsDeleting(false);
      setConfirmDelete(false);
    }
  };

  // ─── Loading ────────────────────────────────────────────────────────────
  if (f.taskLoading && !f.loadedTask) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="w-full max-w-2xl space-y-4 motion-safe:animate-pulse">
          <div className="h-8 w-1/3 rounded bg-muted" />
          <div className="h-4 w-1/2 rounded bg-muted" />
          <div className="h-64 rounded bg-muted" />
        </div>
      </div>
    );
  }

  // ─── The task could not be read ─────────────────────────────────────────
  if (f.taskError && !f.loadedTask) {
    return (
      <>
        <ErrorAlert error={f.taskError} />
        <Link to="/tasks" className="mt-2 inline-block text-sm text-primary underline">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  if (!f.loadedTask) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/tasks" className="mt-2 inline-block text-sm text-primary underline">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  const taskTitle = f.loadedTask.title || t('task.details');

  // The action bar mirrors the create page's, with the two differences that
  // are real: the button promises a save rather than a creation, and a task
  // that exists can also be removed.
  const actionBar = (
    <div className="sticky bottom-0 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-background/95 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          disabled={f.isSubmitting || isDeleting}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
        >
          <Icon d={FORM_ICONS.trash} className="h-3.5 w-3.5" />
          {t('task.delete')}
        </button>
        <p className="min-w-0 text-xs" aria-live="polite">
          {f.missingRequired.length > 0 ? (
            <span className="text-destructive">
              {t('task.createForm.requiredRemaining', {
                count: f.missingRequired.length,
                fields: f.missingRequired.join(', '),
              })}
            </span>
          ) : (
            <span className="text-muted-foreground">
              {f.isDirty ? t('task.form.unsavedChanges') : t('task.form.saveHint')}
            </span>
          )}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={() => (f.isDirty ? setConfirmLeave(true) : leave())}
        >
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={!f.canSubmit} isLoading={f.isSubmitting}>
          {t('common.saveChanges')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto w-full max-w-5xl pb-24">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav aria-label={t('layout.navbar.breadcrumb')} className="flex items-center gap-2 text-sm">
          <Link
            to="/tasks"
            className="rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('task.list')}
          </Link>
          <span className="text-muted-foreground" aria-hidden="true">
            /
          </span>
          <Link
            to={`/tasks/${id}`}
            className="max-w-[16rem] truncate rounded-sm text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {taskTitle}
          </Link>
          <span className="text-muted-foreground" aria-hidden="true">
            /
          </span>
          <h1 aria-current="page" className="font-medium text-foreground">
            {t('common.edit')}
          </h1>
        </nav>
      </div>

      <TaskFormComposer form={f} idPrefix="task-edit" actions={actionBar} />

      <ConfirmDialog
        open={confirmLeave}
        title={t('task.form.leaveTitle')}
        description={t('task.form.leaveDescription')}
        confirmLabel={t('task.form.leaveConfirm')}
        cancelLabel={t('task.form.leaveCancel')}
        destructive
        onConfirm={() => {
          setConfirmLeave(false);
          leave();
        }}
        onCancel={() => setConfirmLeave(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('task.deleteTitle')}
        description={t('task.deleteConfirm')}
        confirmLabel={t('task.delete')}
        cancelLabel={t('common.cancel')}
        destructive
        isConfirming={isDeleting}
        onConfirm={() => void handleDelete()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
});

export default TaskEditPage;
